import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import moment from "moment";
import API from "../../services/SchoolClassService";
import * as teacherActions from "../../redux/actions/teacherActions";
import * as lessonRegisterActions from "../../redux/actions/lessonRegisterActions";
import * as schoolClassActions from "../../redux/actions/schoolClassActions";
import * as studentActions from "../../redux/actions/studentActions";
import * as classStudentsActions from "../../redux/actions/classStudentsActions";
import * as bookActions from "../../redux/actions/bookActions";
import { STATUS, CLASS_TYPES, WEEKDAYS } from "../common/Const";
import DashboardCalendar from "./DashboardCalendar";
import SelectorModal from "../common/SelectorModal";
import CreateClassModal from "./CreateClassModal";

const Dashboard = ({
  userId,
  schoolClasses,
  loadSchoolClasses,
  saveSchoolClass,
  teachers,
  loadTeachers,
  students,
  loadStudents,
  classStudents,
  loadClassStudents,
  saveLessonRegister,
  loadLessonRegisters,
  deleteLessonRegister,
  lessonRegister,
  books,
  loadBooks,
}) => {
  const [user, setUser] = useState({});
  const [selectedTeacher, setSelectedTeacher] = useState(user);
  const [scheduledClasses, setScheduledClasses] = useState([]);
  const [status, setStatus] = useState(STATUS.VIEW);
  const [errors, setErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedSlotData, setSelectedSlotData] = useState(null);

  useEffect(() => {
    if (schoolClasses.length === 0) {
      loadSchoolClasses().catch((error) => {
        alert(`Loading schoolClasses failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers().catch((error) => {
        alert(`Loading teachers failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    const teacher = teachers.find(t => t.email === userId);
    if (teacher) {
      setUser(teacher);
      setSelectedTeacher({ value: teacher.id, text: `${teacher.firstName} ${teacher.lastName}` });
    }
  }, [teachers, userId]);

  useEffect(() => {
    if (lessonRegister.length === 0) {
      loadLessonRegisters().catch((error) => {
        alert(`Loading lessonRegisters failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    if (classStudents.length === 0) {
      loadClassStudents().catch((error) => {
        alert(`Loading classStudents failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    if (students.length === 0) {
      loadStudents().catch((error) => {
        alert(`Loading students failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    if (books.length === 0) {
      loadBooks().catch((error) => {
        alert(`Loading books failed ${error}`);
      });
    }
  }, []);

  useEffect(() => {
    const updateScheduledClasses = () => {
      const FilteredClasses = schoolClasses.filter(
        schoolClass => schoolClass.teacherId === selectedTeacher.value
      );

      const generatedClasses = FilteredClasses.map((schoolClass) => {
        const scheduledLessons = API.generateScheduledClasses(
          schoolClass,
          lessonRegister,
          students,
          classStudents
        );
        return {
          scheduledLessons,
        };
      });
      setScheduledClasses(generatedClasses);
    };

    updateScheduledClasses();
  }, [schoolClasses, lessonRegister, classStudents, students, selectedTeacher]);

  const handleTeacherSelect = (option) => {
    setSelectedTeacher(option);
    setIsModalOpen(false);
  };

  const handleRegisterUpdate = async (event) => {
    saveLessonRegister(event.lessonRegister)
      .then((savedRegister) => {
        setStatus(STATUS.VIEW);
      })
      .catch((error) => {
        setErrors({ ...errors, saveError: error.message });
        setStatus(STATUS.SUBMITTED);
      });
  };

  const handleCreateClass = (classData) => {
    const classPayload = {
      ...classData,
      fromDate: classData.fromDate || moment().format('YYYY-MM-DD'),
    };
    
    return saveSchoolClass(classPayload)
      .then(() => {
        loadSchoolClasses();
        setIsCreateModalOpen(false);
      });
  };

  return (
    <div className="mb-5">
      <h2 className="manage-title col-block mb-0">
        Witaj {user.firstName || userId}
      </h2>
      <h4 className="col-block d-flex align-items-center mb-3">
        Oglądasz: &nbsp;
        {selectedTeacher.text || `${userId}`}
        <button
          type="button"
          className="manage-select-button"
          onClick={() => setIsModalOpen(true)}
        >
          Wybierz nauczyciela
        </button>
      </h4>
      <SelectorModal
        title="Wybierz nauczyciela"
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        values={teachers.map(t => ({
          value: t.id,
          text: `${t.firstName} ${t.lastName}`,
        }))}
        onSelect={option => handleTeacherSelect(option)}
        selectedIDs={[selectedTeacher.value || user.id]}
      />
      <DashboardCalendar
        scheduledClasses={scheduledClasses}
        teachers={teachers}
        students={students}
        onSubmit={handleRegisterUpdate}
        deleteLessonRegister={deleteLessonRegister}
      />
      <CreateClassModal
        isOpen={isCreateModalOpen}
        initialDate={selectedSlotData?.date}
        initialTime={{ start: selectedSlotData?.startTime, end: selectedSlotData?.endTime }}
        initialDayOfWeek={selectedSlotData?.dayOfWeek}
        initialTeacherId={selectedTeacher.value}
        classTypes={CLASS_TYPES}
        teachers={teachers}
        books={books}
        weekdays={WEEKDAYS}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateClass}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  schoolClasses: state.schoolClasses,
  teachers: state.teachers,
  students: state.students,
  classStudents: state.classStudents,
  lessonRegister: state.lessonRegister,
  books: state.books,
});

const mapDispatchToProps = {
  loadSchoolClasses: schoolClassActions.loadSchoolClasses,
  saveSchoolClass: schoolClassActions.saveSchoolClass,
  loadBooks: bookActions.loadBooks,
  loadClassStudents: classStudentsActions.loadClassStudents,
  loadTeachers: teacherActions.loadTeachers,
  loadStudents: studentActions.loadStudents,
  saveLessonRegister: lessonRegisterActions.saveLessonRegister,
  loadLessonRegisters: lessonRegisterActions.loadLessonRegisters,
  deleteLessonRegister: lessonRegisterActions.deleteLessonRegister,
};

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
